<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { reactive, ref, toRaw } from 'vue';

import KTextField from '@/components/fields/KTextField.vue';
import KTextareaField from '@/components/fields/KTextareaField.vue';
import SuburbAutocompleteField from '@/components/fields/SuburbAutocompleteField.vue';
import useForm from '@/composables/useForm';
import {
  getMaxLengthValidationRule,
  requiredValidationRule,
} from '@/helpers/validationRules';

export interface InitialData {
  name?: string;
  line2?: string;
  line1?: string;
  suburb?: openapi.SuburbOpenapi | null;
  noteName?: string;
  notePhone?: string;
  noteText?: string;
}

export interface ValidData {
  name?: string;
  line2?: string;
  line1?: string;
  suburb: openapi.SuburbOpenapi;
  noteName?: string;
  notePhone?: string;
  noteText?: string;
}

export interface Props {
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

// 30 = legacy RH field cap; phone/text limits mirror the backend Address model
const ADDRESS_FIELD_MAX_LENGTH = 30;
const NOTE_PHONE_MAX_LENGTH = 20;
const NOTE_TEXT_MAX_LENGTH = 150;

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, function (event) {
  emit('submit', event, toRaw(formData) as ValidData);
});
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.addressBookAddressDialogForm"
    data-testid="addressBookAddressDialogForm"
    @submit.prevent="handleSubmit"
  >
    <KTextField
      v-model="formData.name"
      label="Address name"
      :rules="[
        requiredValidationRule,
        getMaxLengthValidationRule(ADDRESS_FIELD_MAX_LENGTH),
      ]"
      :maxlength="ADDRESS_FIELD_MAX_LENGTH"
      :class="$style.field"
    />
    <KTextField
      v-model="formData.line2"
      label="Street address"
      :rules="[
        requiredValidationRule,
        getMaxLengthValidationRule(ADDRESS_FIELD_MAX_LENGTH),
      ]"
      :maxlength="ADDRESS_FIELD_MAX_LENGTH"
      :class="$style.field"
    />
    <KTextField
      v-model="formData.line1"
      label="Room / Level / Unit"
      :rules="[getMaxLengthValidationRule(ADDRESS_FIELD_MAX_LENGTH)]"
      :maxlength="ADDRESS_FIELD_MAX_LENGTH"
      :class="$style.field"
    />
    <SuburbAutocompleteField
      v-model="formData.suburb"
      :rules="[requiredValidationRule]"
      :class="[$style.field, $style.suburbField]"
    />
    <KTextField
      v-model="formData.noteName"
      label="Note name"
      :rules="[getMaxLengthValidationRule(ADDRESS_FIELD_MAX_LENGTH)]"
      :maxlength="ADDRESS_FIELD_MAX_LENGTH"
      :class="$style.field"
    />
    <KTextField
      v-model="formData.notePhone"
      label="Note phone"
      :rules="[getMaxLengthValidationRule(NOTE_PHONE_MAX_LENGTH)]"
      :maxlength="NOTE_PHONE_MAX_LENGTH"
      :class="$style.field"
    />
    <KTextareaField
      v-model="formData.noteText"
      label="Note text"
      :rules="[getMaxLengthValidationRule(NOTE_TEXT_MAX_LENGTH)]"
      :maxlength="NOTE_TEXT_MAX_LENGTH"
      :class="$style.field"
    />
    <input type="submit" class="hidden" />
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.addressBookAddressDialogForm {
}
  .field {
  }
</style>
